import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Send, Mail, FileText, Paperclip, File } from 'lucide-react';
import { useCommunicationStore } from '../../store/communicationStore';
import { usePartnershipsStore } from '../../store/partnershipsStore';
import { useAuthStore } from '../../store/authStore';
import { communicationService } from '../../services/communication.service';
import { toast } from 'sonner';

interface EmailFormData {
    to: string[];
    cc: string[];
    subject: string;
    bodyHtml: string;
    templateId: string;
    partnershipId: string;
    attachments: File[];
}

interface AttachmentInfo {
    name: string;
    url?: string;
    type?: string;
}

interface ComposeEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    preselectedTemplate?: string;
    preselectedPartnership?: string;
    prefilledSubject?: string;
    prefilledBody?: string;
    prefilledTo?: string[];
    prefilledAttachments?: AttachmentInfo[];
}

const ComposeEmailModal: React.FC<ComposeEmailModalProps> = ({
    isOpen,
    onClose,
    preselectedTemplate,
    preselectedPartnership,
    prefilledSubject,
    prefilledBody,
    prefilledTo,
    prefilledAttachments,
}) => {
    const { templates, sendEmail, fetchEmails, isLoading } = useCommunicationStore();
    const { partnerships, fetchPartnerships } = usePartnershipsStore();
    const { user, team } = useAuthStore();

    const [gmailConnected, setGmailConnected] = useState(false);
    const [sending, setSending] = useState(false);

    const [formData, setFormData] = useState<EmailFormData>({
        to: [],
        cc: [],
        subject: '',
        bodyHtml: '',
        templateId: preselectedTemplate || '',
        partnershipId: preselectedPartnership || '',
        attachments: [],
    });

    const [toInput, setToInput] = useState('');
    const [ccInput, setCcInput] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [prefilledAttachmentsList, setPrefilledAttachmentsList] = useState<AttachmentInfo[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Track if prefilled content was provided - if so, don't overwrite with template placeholders
    const usePrefilledContentRef = useRef<boolean>(false);

    // Helper function to replace variables with actual values
    const replaceVariables = useCallback((text: string, partnershipId?: string) => {
        const partnership = partnershipId
            ? partnerships.find(p => String(p.id) === partnershipId)
            : null;

        let result = text;

        // Partnership/Brand variables
        if (partnership) {
            result = result.replace(/\{\{partnership\.brand\}\}/g, partnership.brand || '');
            result = result.replace(/\{\{partnership\.contact\}\}/g, partnership.contact || '');
            result = result.replace(/\{\{partnership\.email\}\}/g, partnership.email || '');
            result = result.replace(/\{\{partnership\.value\}\}/g, String(partnership.value || ''));
            result = result.replace(/\{\{partnership\.stage\}\}/g, partnership.stage || '');
            result = result.replace(/\{\{partnership\.contentType\}\}/g, partnership.contentType || '');
            result = result.replace(/\{\{partnership\.phone\}\}/g, partnership.phone || '');
            // Legacy variable names
            result = result.replace(/\{\{brand\.name\}\}/g, partnership.brand || '');
            result = result.replace(/\{\{contact\.firstName\}\}/g, partnership.contact?.split(' ')[0] || '');
            result = result.replace(/\{\{contact\.email\}\}/g, partnership.email || '');
        }

        // User variables
        if (user) {
            result = result.replace(/\{\{user\.firstName\}\}/g, user.firstName || '');
            result = result.replace(/\{\{user\.lastName\}\}/g, user.lastName || '');
            result = result.replace(/\{\{user\.email\}\}/g, user.email || '');
        }

        // Team variables
        if (team) {
            result = result.replace(/\{\{team\.name\}\}/g, team.name || '');
        }

        return result;
    }, [partnerships, user, team]);

    // Sync preselectedPartnership and prefilled values with form data when modal opens
    useEffect(() => {
        if (isOpen) {
            // Track if prefilled subject/body content was provided
            const hasPrefilled = Boolean(prefilledSubject || prefilledBody);
            usePrefilledContentRef.current = hasPrefilled;

            setFormData(prev => ({
                ...prev,
                ...(preselectedPartnership && { partnershipId: preselectedPartnership }),
                ...(prefilledSubject && { subject: prefilledSubject }),
                ...(prefilledBody && { bodyHtml: prefilledBody }),
                ...(prefilledTo && { to: prefilledTo }),
                ...(preselectedTemplate && { templateId: preselectedTemplate }),
            }));
            // Set prefilled attachments info
            if (prefilledAttachments) {
                setPrefilledAttachmentsList(prefilledAttachments);
            }
            // Pre-select template in dropdown (but don't apply its content if prefilled was provided)
            if (preselectedTemplate) {
                setSelectedTemplateId(preselectedTemplate);
            }
        }
    }, [isOpen, preselectedPartnership, prefilledSubject, prefilledBody, prefilledTo, prefilledAttachments, preselectedTemplate]);

    useEffect(() => {
        if (isOpen) {
            fetchPartnerships();
            // Check Gmail connection status
            communicationService.getGmailStatus().then(status => {
                setGmailConnected(status.connected);
            });
        }
    }, [isOpen, fetchPartnerships]);

    // When template is selected, apply it with variable replacement
    // Skip if prefilled content was provided (don't overwrite actual values with placeholders)
    useEffect(() => {
        if (selectedTemplateId) {
            // If prefilled content was provided, don't overwrite with template placeholders
            if (usePrefilledContentRef.current) {
                // Reset the ref so future template selections work normally
                usePrefilledContentRef.current = false;
                return;
            }

            const template = templates.find(t => t.id === selectedTemplateId);
            if (template) {
                const subject = replaceVariables(template.subject, formData.partnershipId || undefined);
                const bodyHtml = replaceVariables(template.bodyHtml, formData.partnershipId || undefined);
                setFormData(prev => ({
                    ...prev,
                    subject,
                    bodyHtml,
                    templateId: template.id,
                }));
            }
        }
    }, [selectedTemplateId, templates, formData.partnershipId, replaceVariables]);

    // When partnership is selected, auto-fill email and re-apply variable replacement
    useEffect(() => {
        if (formData.partnershipId) {
            const partnership = partnerships.find(p => String(p.id) === formData.partnershipId);
            if (partnership && partnership.email && !formData.to.includes(partnership.email)) {
                // Auto-fill recipient
                setFormData(prev => ({
                    ...prev,
                    to: [partnership.email, ...prev.to.filter(e => e !== partnership.email)],
                }));
            }
            // Re-apply variable replacement if we have a template selected
            if (selectedTemplateId) {
                const template = templates.find(t => t.id === selectedTemplateId);
                if (template) {
                    const subject = replaceVariables(template.subject, formData.partnershipId);
                    const bodyHtml = replaceVariables(template.bodyHtml, formData.partnershipId);
                    setFormData(prev => ({
                        ...prev,
                        subject,
                        bodyHtml,
                    }));
                }
            }
        }
    }, [formData.partnershipId, partnerships]);

    const handleAddTo = () => {
        if (toInput && toInput.includes('@') && !formData.to.includes(toInput)) {
            setFormData(prev => ({ ...prev, to: [...prev.to, toInput] }));
            setToInput('');
        }
    };

    const handleAddCc = () => {
        if (ccInput && ccInput.includes('@') && !formData.cc.includes(ccInput)) {
            setFormData(prev => ({ ...prev, cc: [...prev.cc, ccInput] }));
            setCcInput('');
        }
    };

    const handleRemoveTo = (email: string) => {
        setFormData(prev => ({ ...prev, to: prev.to.filter(e => e !== email) }));
    };

    const handleRemoveCc = (email: string) => {
        setFormData(prev => ({ ...prev, cc: prev.cc.filter(e => e !== email) }));
    };

    const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const newFiles = Array.from(files).filter(file => {
                // Allow PDF and common document types
                const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                if (!allowedTypes.includes(file.type)) {
                    toast.error(`${file.name}: Only PDF, images, and documents are allowed`);
                    return false;
                }
                // Max 10MB per file
                if (file.size > 10 * 1024 * 1024) {
                    toast.error(`${file.name}: File size must be less than 10MB`);
                    return false;
                }
                return true;
            });
            setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ...newFiles] }));
        }
        // Reset input so same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveAttachment = (index: number) => {
        setFormData(prev => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index)
        }));
    };

    const handleRemovePrefilledAttachment = (index: number) => {
        setPrefilledAttachmentsList(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent, logOnly: boolean = false) => {
        e.preventDefault();
        if (formData.to.length === 0) return;

        setSending(true);
        try {
            if (gmailConnected && !logOnly) {
                // Send via Gmail API - this also saves to DB on backend
                await communicationService.sendViaGmail({
                    to: formData.to,
                    subject: formData.subject,
                    bodyHtml: formData.bodyHtml,
                    ...(formData.cc.length > 0 && { cc: formData.cc }),
                    ...(formData.partnershipId && { partnershipId: formData.partnershipId }),
                    ...(formData.templateId && { templateId: formData.templateId }),
                });
                toast.success('Email sent via Gmail!');
            } else {
                // Just log to DB (no actual send without Gmail connection)
                await sendEmail({
                    to: formData.to,
                    cc: formData.cc.length > 0 ? formData.cc : undefined,
                    subject: formData.subject,
                    bodyHtml: formData.bodyHtml,
                    templateId: formData.templateId || undefined,
                    partnershipId: formData.partnershipId || undefined,
                });
                toast.info('Email logged successfully');
            }
            // Refresh email list
            fetchEmails();
            onClose();
            resetForm();
        } catch (error) {
            console.error('Failed to send email:', error);
            toast.error('Failed to send email. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const resetForm = () => {
        setFormData({
            to: [],
            cc: [],
            subject: '',
            bodyHtml: '',
            templateId: '',
            partnershipId: '',
            attachments: [],
        });
        setToInput('');
        setCcInput('');
        setSelectedTemplateId('');
        setPrefilledAttachmentsList([]);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-xl flex items-center justify-center">
                            <Mail className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Compose Email</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                    {/* Partnership Selector */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Partnership (Optional)
                        </label>
                        <select
                            value={formData.partnershipId}
                            onChange={(e) => setFormData(prev => ({ ...prev, partnershipId: e.target.value }))}
                            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        >
                            <option value="">Select a partnership...</option>
                            {partnerships.map(p => (
                                <option key={p.id} value={String(p.id)}>{p.brand} - {p.contact}</option>
                            ))}
                        </select>
                    </div>

                    {/* To Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            To *
                        </label>
                        <div className="flex flex-wrap gap-2 p-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 min-h-[42px]">
                            {formData.to.map(email => (
                                <span
                                    key={email}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg text-sm"
                                >
                                    {email}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTo(email)}
                                        className="hover:text-primary-900 dark:hover:text-primary-100"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                            <input
                                type="email"
                                value={toInput}
                                onChange={(e) => setToInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTo())}
                                onBlur={handleAddTo}
                                placeholder="Add email..."
                                className="flex-1 min-w-[150px] bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
                            />
                        </div>
                    </div>

                    {/* CC Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            CC
                        </label>
                        <div className="flex flex-wrap gap-2 p-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 min-h-[42px]">
                            {formData.cc.map(email => (
                                <span
                                    key={email}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm"
                                >
                                    {email}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveCc(email)}
                                        className="hover:text-gray-900 dark:hover:text-gray-100"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                            <input
                                type="email"
                                value={ccInput}
                                onChange={(e) => setCcInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCc())}
                                onBlur={handleAddCc}
                                placeholder="Add CC..."
                                className="flex-1 min-w-[150px] bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
                            />
                        </div>
                    </div>

                    {/* Template Selector */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Template
                        </label>
                        <select
                            value={selectedTemplateId}
                            onChange={(e) => setSelectedTemplateId(e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        >
                            <option value="">Select a template...</option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
                            ))}
                        </select>
                    </div>

                    {/* Subject */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Subject *
                        </label>
                        <input
                            type="text"
                            value={formData.subject}
                            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                            required
                            placeholder="Email subject..."
                            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        />
                    </div>

                    {/* Body */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Message *
                        </label>
                        <textarea
                            value={formData.bodyHtml}
                            onChange={(e) => setFormData(prev => ({ ...prev, bodyHtml: e.target.value }))}
                            required
                            rows={8}
                            placeholder="Write your message..."
                            className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Use {"{{variable}}"} syntax for dynamic content
                        </p>
                    </div>

                    {/* Attachments */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Attachments
                        </label>

                        {/* Prefilled attachments (from invoice/contract etc) */}
                        {prefilledAttachmentsList.length > 0 && (
                            <div className="mb-2 space-y-2">
                                {prefilledAttachmentsList.map((att, idx) => (
                                    <div
                                        key={`prefilled-${idx}`}
                                        className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                                    >
                                        <div className="flex items-center gap-2">
                                            <File className="w-4 h-4 text-blue-500" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{att.name}</span>
                                            <span className="text-xs text-blue-600 dark:text-blue-400">(Invoice PDF)</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleRemovePrefilledAttachment(idx)}
                                            className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded transition-colors"
                                        >
                                            <X className="w-3 h-3 text-gray-500" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Uploaded attachments */}
                        {formData.attachments.length > 0 && (
                            <div className="mb-2 space-y-2">
                                {formData.attachments.map((file, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-700 rounded-lg"
                                    >
                                        <div className="flex items-center gap-2">
                                            <File className="w-4 h-4 text-gray-500" />
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                                            <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(1)} KB)</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveAttachment(idx)}
                                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                                        >
                                            <X className="w-3 h-3 text-gray-500" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* File input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleAttachmentChange}
                            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                            multiple
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        >
                            <Paperclip className="w-4 h-4" />
                            Add Attachment
                        </button>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Max 10MB per file. Supported: PDF, PNG, JPG, DOC, DOCX
                        </p>
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors"
                    >
                        Cancel
                    </button>
                    <div className="flex items-center gap-2">
                        {gmailConnected && (
                            <button
                                type="button"
                                onClick={(e) => handleSubmit(e, true)}
                                disabled={sending || isLoading || formData.to.length === 0 || !formData.subject || !formData.bodyHtml}
                                className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FileText className="w-4 h-4" />
                                Log Email
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={(e) => handleSubmit(e, false)}
                            disabled={sending || isLoading || formData.to.length === 0 || !formData.subject || !formData.bodyHtml}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Send className="w-4 h-4" />
                            {sending ? 'Sending...' : gmailConnected ? 'Send via Gmail' : 'Log Email'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComposeEmailModal;
